import { useState } from "react";
import { useIntl } from "react-intl";

// Password-masked text input with a reveal (eye) toggle. Built from the same
// field tokens as the rest of the form controls (cf. the hex input in
// SettingsPage's ColorPicker): rounded bg-surface, hairline ring, ink/30 focus
// ring. Masked by default; the toggle flips type between password and text.
// Value is mono/tabular so long keys read cleanly (matches the hex field).

interface SecretInputProps {
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  id?: string;
  placeholder?: string;
  autoComplete?: string;
}

export function SecretInput({
  value,
  onChange,
  ariaLabel,
  id,
  placeholder,
  autoComplete = "off",
}: SecretInputProps) {
  const intl = useIntl();
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="relative">
      <input
        id={id}
        type={revealed ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        className="smycles-field-value w-full rounded bg-surface px-3 py-2.5 pr-10 font-mono tabular-nums ring-1 ring-ink-ui focus:outline-none focus:ring-2 focus:ring-ink"
      />
      <button
        type="button"
        onClick={() => setRevealed((r) => !r)}
        aria-pressed={revealed}
        aria-label={
          revealed
            ? intl.formatMessage({ id: "secretInput.hide", defaultMessage: "Hide value" })
            : intl.formatMessage({ id: "secretInput.show", defaultMessage: "Show value" })
        }
        className="absolute right-1.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded text-ink-muted transition hover:text-ink"
      >
        {revealed ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path d="M9.88 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
